import { useState, useCallback } from 'react';
import axios from 'axios';
import { User, LoginRequest, RegisterRequest, LoginResponse, AuthError } from '../types/auth';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// JWT token storage key
const TOKEN_STORAGE_KEY = 'subscnavi_token';

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // トークンをlocalStorageから取得
  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }, []);

  // トークンをlocalStorageに保存
  const setToken = useCallback((token: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }, []);

  // トークンをlocalStorageから削除
  const removeToken = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  // ログイン処理
  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post<LoginResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`,
        credentials,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { access_token } = response.data;
      setToken(access_token);
      
      // ログイン成功時にユーザー情報を取得
      await getCurrentUser(access_token);
      
      return true;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.detail || 'ログインに失敗しました';
        setError({
          message,
          status: err.response?.status,
        });
      } else {
        setError({
          message: 'ネットワークエラーが発生しました',
        });
      }
      return false;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setToken]);

  // サインアップ処理
  const register = useCallback(async (userData: RegisterRequest): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await axios.post<User>(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`,
        userData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // サインアップ成功後、自動的にログインを実行
      const loginSuccess = await login({
        email: userData.email,
        password: userData.password,
      });

      return loginSuccess;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const message = err.response?.data?.detail || 'アカウント作成に失敗しました';
        setError({
          message,
          status: err.response?.status,
        });
      } else {
        setError({
          message: 'ネットワークエラーが発生しました',
        });
      }
      return false;
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  // 現在のユーザー情報を取得
  const getCurrentUser = useCallback(async (token?: string): Promise<User | null> => {
    const authToken = token || getToken();
    if (!authToken) {
      return null;
    }

    try {
      const response = await axios.get<User>(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.ME}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      setUser(response.data);
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        // トークンが無効な場合はログアウト
        logout();
      }
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  // ログアウト処理
  const logout = useCallback(() => {
    removeToken();
    setUser(null);
    setError(null);
  }, [removeToken]);

  // ユーザーがログイン済みかどうかを判定
  const isAuthenticated = useCallback(() => {
    return !!getToken() && !!user;
  }, [getToken, user]);

  // 初期化：アプリ起動時にトークンがあれば自動ログイン試行
  const initialize = useCallback(async () => {
    const token = getToken();
    if (token) {
      await getCurrentUser(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  return {
    // State
    isLoading,
    error,
    user,

    // Actions
    login,
    register,
    logout,
    getCurrentUser,
    initialize,
    isAuthenticated,

    // Utils
    getToken,
  };
};
